import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { ImageMetrics } from '@ai-color-modules/simulation/hooks/useImageMetrics'

/** パターン画像（mask.webp 338B）をデータURLとしてインライン化 */
const MASK_PATTERN_DATA_URL =
  'data:image/webp;base64,UklGRkoBAABXRUJQVlA4WAoAAAASAAAAFwAAFwAAQU5JTQYAAAD/////AABBTk1GlAAAAAAAAAAAABcAABcAAFgCAAJBTFBIOQAAAAEnoJBtBMhf7wAO5NeIiLgEQlEkSU3dKUgGDlAAGta/LDK8I/ovICjywAwcy1sPSw9FT6nY2qcOAABWUDggOgAAADADAJ0BKhgAGAA+kUKcSiWjoqGoCACwEgllAMgSgABAUFAA/teV//npn9bfx/2ffi2KQ2wTn0dAAABBTk1GggAAAAIAAAIAAA8AAA8AAFgCAAJBTFBINQAAAAEnoJBtBMhf7wAO5NeIiLgEQlEkqVGBgssASRSABvyrmmcERPR/AnIW1UiKXu9Xc0iNpOg1AFZQOCAsAAAAkAEAnQEqEAAQAAIANCWgAnS6AAOYAP7guX/2Dv/0Hf/oO/1TP0fXCLICAAA='

/** パターンのタイルサイズ（px） */
const PATTERN_SIZE = 12

interface Point {
  originalX: number
  originalY: number
}

/**
 * ポリゴン群をオリジナル画像座標系から canvas 座標系へスケールして塗る。
 * union（塗り足し）と cutout（切り抜き）で座標変換・パス描画を共有し、
 * 幾何演算のズレ（片側だけ修正される事故）を防ぐ。合成モードは呼び出し側で設定する。
 */
const tracePolygons = (
  ctx: CanvasRenderingContext2D,
  polygons: Point[][],
  canvasWidth: number,
  canvasHeight: number,
  originalDimensions: { width: number; height: number },
) => {
  const scaleX = canvasWidth / originalDimensions.width
  const scaleY = canvasHeight / originalDimensions.height

  for (const polygon of polygons) {
    if (polygon.length < 3) continue

    ctx.beginPath()
    ctx.moveTo(polygon[0].originalX * scaleX, polygon[0].originalY * scaleY)
    for (let i = 1; i < polygon.length; i++) {
      ctx.lineTo(polygon[i].originalX * scaleX, polygon[i].originalY * scaleY)
    }
    ctx.closePath()
    ctx.fill()
  }
}

/**
 * グレースケール（輝度）マスク画像をアルファマスクに変換し、
 * オプションでポリゴン部分を差し引く（destination-out）
 *
 * iOS WebKit は CSS mask-mode: luminance を正しくサポートしないため、
 * Canvas で輝度値をアルファチャンネルにコピーした PNG data URL を生成する。
 */
const useLuminanceToAlphaMask = (
  maskUrl: string | undefined,
  excludePolygons?: Point[][],
  originalDimensions?: { width: number; height: number },
  includePolygons?: Point[][],
) => {
  const [alphaMaskUrl, setAlphaMaskUrl] = useState<string | undefined>()
  const cacheRef = useRef<Record<string, string>>({})

  // excludePolygons のキャッシュキーを生成
  const excludeKey = useMemo(() => {
    if (!excludePolygons || excludePolygons.length === 0) return ''
    return JSON.stringify(
      excludePolygons.map(poly => poly.map(p => `${p.originalX},${p.originalY}`)),
    )
  }, [excludePolygons])

  // includePolygons（統合ポリゴン, issue #2224）のキャッシュキーを生成
  const includeKey = useMemo(() => {
    if (!includePolygons || includePolygons.length === 0) return ''
    return JSON.stringify(
      includePolygons.map(poly => poly.map(p => `${p.originalX},${p.originalY}`)),
    )
  }, [includePolygons])

  // 統合ポリゴン（issue #2224）をマスクに union（塗り足し）する処理.
  // ベースエリアに統合されたカスタムエリア領域を、当該ベースエリアの一部として表示する.
  const fillInPolygons = useCallback(
    (ctx: CanvasRenderingContext2D, canvasWidth: number, canvasHeight: number) => {
      if (!includePolygons || includePolygons.length === 0 || !originalDimensions) return

      ctx.globalCompositeOperation = 'source-over'
      ctx.fillStyle = '#ffffff' // 不透明白 = alpha 255（色変換対象）
      tracePolygons(ctx, includePolygons, canvasWidth, canvasHeight, originalDimensions)
    },
    [includePolygons, originalDimensions],
  )

  // ポリゴンを切り抜く処理
  const cutOutPolygons = useCallback(
    (ctx: CanvasRenderingContext2D, canvasWidth: number, canvasHeight: number) => {
      if (!excludePolygons || excludePolygons.length === 0 || !originalDimensions) return

      // destination-out で切り抜き
      ctx.globalCompositeOperation = 'destination-out'
      ctx.fillStyle = '#ffffff'
      tracePolygons(ctx, excludePolygons, canvasWidth, canvasHeight, originalDimensions)

      // 合成モードをリセット
      ctx.globalCompositeOperation = 'source-over'
    },
    [excludePolygons, originalDimensions],
  )

  useEffect(() => {
    if (!maskUrl) {
      setAlphaMaskUrl(undefined)
      return
    }

    // キャッシュキー（マスクURL + includePolygons + excludePolygons）
    const fullCacheKey = `${maskUrl}__${includeKey}__${excludeKey}`

    // キャッシュにあればそのまま使用
    if (cacheRef.current[fullCacheKey]) {
      setAlphaMaskUrl(cacheRef.current[fullCacheKey])
      return
    }

    const img = new Image()
    img.crossOrigin = 'anonymous'

    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = img.naturalWidth
      canvas.height = img.naturalHeight
      const ctx = canvas.getContext('2d')
      if (!ctx) return

      ctx.drawImage(img, 0, 0)
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
      const { data } = imageData

      // R チャンネルの輝度値を A チャンネルにコピーし、RGB は白に
      for (let i = 0; i < data.length; i += 4) {
        const luminance = data[i] // R (グレースケールなので R=G=B)
        data[i] = 255 // R
        data[i + 1] = 255 // G
        data[i + 2] = 255 // B
        data[i + 3] = luminance // A = 元の輝度
      }

      ctx.putImageData(imageData, 0, 0)

      // 統合ポリゴン（issue #2224）をベースマスクに union（先に塗り足し）
      fillInPolygons(ctx, canvas.width, canvas.height)

      // 残りのカスタムエリアポリゴン部分を切り抜く
      cutOutPolygons(ctx, canvas.width, canvas.height)

      const dataUrl = canvas.toDataURL('image/png')
      cacheRef.current[fullCacheKey] = dataUrl
      setAlphaMaskUrl(dataUrl)
    }

    img.onerror = () => {
      // 変換失敗時は元のURLをフォールバック（luminance対応ブラウザでは動作する）
      setAlphaMaskUrl(maskUrl)
    }

    img.src = maskUrl
  }, [maskUrl, excludeKey, includeKey, cutOutPolygons, fillInPolygons])

  return alphaMaskUrl
}

interface MaskColorOverlayProps {
  /** ベースエリアごとのマスク画像URL (例: { "外壁": "https://..." }) */
  maskUrls: Record<string, string>
  /** フォーカス中のベースエリア名（nullの場合は非表示） */
  focusedArea?: string | null
  /** 画像の表示メトリクスを取得する関数 */
  getMetrics: () => ImageMetrics
  /** マスクから差し引くカスタムエリアポリゴン */
  excludePolygons?: Point[][]
  /** マスクに union する統合ポリゴン（issue #2224。当該ベースエリアに統合されたカスタムエリア領域） */
  includePolygons?: Point[][]
  /** オリジナル画像のサイズ（excludePolygons / includePolygons 使用時に必要） */
  originalDimensions?: { width: number; height: number }
}

/**
 * マスク画像を元にパターン透過レイヤーをオリジナル画像に被せるコンポーネント
 *
 * ベースエリア（外壁/屋根/雨樋/破風・鼻隠し）フォーカス時のオーバーレイ表示に使用。
 *
 * ## SSOT仕様
 * マスク演算仕様: doc/architecture/ai-color-mask-operations.md
 *
 * ## 重要: Python側との一貫性
 * このオーバーレイは「色指定が実際に適用される範囲」を施主に正確に伝える目的がある。
 * 演算式を変更する場合は、Python側と必ず同期すること。
 *
 * ## マスク演算式
 * - 外壁/屋根: ベースマスク ∩ ¬(カスタムエリアポリゴン)
 * - 雨樋: 雨樋マスク（カスタムエリアによる切り抜きなし、破風以外より優先）
 * - 破風・鼻隠し: 破風マスク（カスタムエリアによる切り抜きなし、最高優先）
 *
 * ## 実装詳細
 * グレースケールマスク画像の輝度をアルファチャンネルに変換（Canvas経由）し、
 * CSS mask-image でパターン画像をクリップする。
 * luminance→alpha 変換により iOS WebKit でも正しく動作する。
 *
 * excludePolygons が指定されている場合、そのポリゴン部分をマスクから切り抜く。
 * 雨樋・破風フォーカス時は呼び出し側で excludePolygons=undefined を渡し、最優先表示を維持する。
 */
export const MaskColorOverlay: React.FC<MaskColorOverlayProps> = ({
  maskUrls,
  focusedArea,
  getMetrics,
  excludePolygons,
  includePolygons,
  originalDimensions,
}) => {
  const metrics = getMetrics()
  const { dispW, dispH, offX, offY } = metrics

  const rawMaskUrl = focusedArea ? maskUrls[focusedArea] : undefined
  const alphaMaskUrl = useLuminanceToAlphaMask(
    rawMaskUrl,
    excludePolygons,
    originalDimensions,
    includePolygons,
  )

  if (!focusedArea || !alphaMaskUrl) return null

  return (
    <div
      data-testid={`mask-overlay-${focusedArea}`}
      style={
        {
          position: 'absolute',
          left: offX,
          top: offY,
          width: dispW,
          height: dispH,
          backgroundImage: `url(${MASK_PATTERN_DATA_URL})`,
          backgroundSize: `${PATTERN_SIZE}px ${PATTERN_SIZE}px`,
          backgroundRepeat: 'repeat',
          WebkitMaskImage: `url(${alphaMaskUrl})`,
          maskImage: `url(${alphaMaskUrl})`,
          WebkitMaskSize: '100% 100%',
          maskSize: '100% 100%',
          WebkitMaskRepeat: 'no-repeat',
          maskRepeat: 'no-repeat',
          pointerEvents: 'none',
        } as React.CSSProperties
      }
    />
  )
}
